import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getContent } from '../i18n/content';
import SiteHeader from '../components/Brand/SiteHeader';
import PageMeta from '../components/Seo/PageMeta';
import JsonLd from '../components/Seo/JsonLd';
import FaqSection from '../components/Seo/FaqSection';
import SizeConverter from '../components/SizeConverter/SizeConverter';
import SizeTable from '../components/SizeConverter/SizeTable';
import ShoppingTips from '../components/SizeConverter/ShoppingTips';
import { menShoeSizes, womenShoeSizes } from '../data/shoeSizes';
import { menClothingSizes, womenClothingSizes } from '../data/clothingSizes';
import { menJeansSizes, womenJeansSizes } from '../data/jeansSizes';
import { kidsShoeSizes } from '../data/kidsShoeSizes';
import QuickNav from '../components/SizeConverter/QuickNav';

const TABLE_CONFIG = {
  shoes: {
    men: { data: menShoeSizes, cols: ['eu', 'uk', 'us', 'cm'], systemId: 'eu' },
    women: { data: womenShoeSizes, cols: ['eu', 'uk', 'us', 'cm'], systemId: 'eu' },
  },
  jeans: {
    men: {
      data: menJeansSizes,
      cols: ['waistIn', 'eu', 'uk', 'us', 'international', 'waistCm'],
      systemId: 'waistIn',
    },
    women: {
      data: womenJeansSizes,
      cols: ['waistIn', 'eu', 'uk', 'us', 'international', 'waistCm'],
      systemId: 'waistIn',
    },
  },
};

export default function SizeConverterPage() {
  const t = getContent();
  const location = useLocation();
  const tables = TABLE_CONFIG;
  const resultLabels = { ...t.systems, ...t.results };

  const [tablePick, setTablePick] = useState(null);
  const [activeRow, setActiveRow] = useState(null);

  const handleTableRow = useCallback((row, meta) => {
    setActiveRow(row);
    setTablePick({
      categoryId: meta.categoryId,
      gender: meta.gender,
      systemId: meta.systemId,
      sizeValue: row[meta.systemId],
    });
  }, []);

  const clearTablePick = useCallback(() => setTablePick(null), []);

  const makeTableHandler = (categoryId, gender, systemId) => (row) =>
    handleTableRow(row, { categoryId, gender, systemId });

  const navLinks = [
    { href: '#size-converter', label: t.nav.converter },
    { href: '#tips', label: t.nav.tips },
    { href: '#shoe-chart', label: t.nav.shoes },
    { href: '#mens-chart', label: t.nav.mens },
    { href: '#womens-chart', label: t.nav.womens },
    { href: '#jeans-chart', label: t.nav.jeans },
    { href: '#kids-chart', label: t.nav.kids },
    { href: '#measure-guide', label: t.nav.measure },
    { href: '#faq', label: t.nav.faq },
  ];

  return (
    <>
      <PageMeta
        title={t.meta.title}
        description={t.meta.description}
        pathname={location.pathname}
      />
      <JsonLd t={t} pathname={location.pathname} />

      <div className="min-h-screen bg-slate-950 text-slate-100">
        <SiteHeader />
        <header className="border-b border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950">
          <div className="max-w-4xl mx-auto px-4 py-10 sm:py-14 text-center">
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="Pixel Utility — clothing and shoe size converter"
              width={96}
              height={96}
              className="mx-auto mb-6 rounded-2xl ring-2 ring-emerald-500/30 shadow-lg shadow-emerald-900/20"
            />
            <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {t.hero.h1}
            </h1>
            <p className="mt-4 text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">
              {t.hero.intro}
            </p>
            <p className="mt-3 text-sm text-amber-400/90 max-w-xl mx-auto">
              {t.hero.disclaimer}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3 text-xs text-slate-500">
              <span className="rounded-full border border-slate-700 px-3 py-1">EU · UK · US</span>
              <span className="rounded-full border border-slate-700 px-3 py-1">XS – XXL</span>
              <span className="rounded-full border border-slate-700 px-3 py-1">Men · Women · Kids</span>
              <span className="rounded-full border border-slate-700 px-3 py-1">Free · No signup</span>
            </div>
            <a
              href="#size-converter"
              className="inline-block mt-8 rounded-xl bg-emerald-600 hover:bg-emerald-500 px-8 py-3.5 font-semibold text-white shadow-lg shadow-emerald-900/30 transition-all hover:scale-[1.02]"
            >
              {t.converter.cta}
            </a>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-10">
          <QuickNav links={navLinks} />

          <SizeConverter t={t} tablePick={tablePick} onTablePickHandled={clearTablePick} />

          <section id="tips" className="mt-8" aria-labelledby="tips-heading">
            <h2 id="tips-heading" className="text-xl font-bold text-white mb-4">
              {t.sections.tipsSectionTitle}
            </h2>
            <ShoppingTips tips={t.tips} />
          </section>

          <article className="mt-16 prose prose-invert max-w-none">
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-white mb-3">{t.sections.howItWorks}</h2>
              <p className="text-slate-400 leading-relaxed">{t.sections.howItWorksText}</p>
            </section>

            <section className="mb-10" id="shoe-chart">
              <h2 className="text-2xl font-bold text-white mb-3">{t.sections.shoes}</h2>
              <p className="text-slate-400 leading-relaxed mb-4">{t.sections.shoesText}</p>
              <h3 className="text-lg font-semibold text-slate-200 mb-2">Men</h3>
              <SizeTable
                title={t.sections.shoes}
                data={tables.shoes.men.data}
                columns={tables.shoes.men.cols}
                columnLabels={resultLabels}
                onRowClick={makeTableHandler('shoes', 'men', 'eu')}
                activeRow={activeRow}
                clickHint={t.table.clickHint}
              />
              <h3 className="text-lg font-semibold text-slate-200 mt-6 mb-2">Women</h3>
              <SizeTable
                title={t.sections.shoes}
                data={tables.shoes.women.data}
                columns={tables.shoes.women.cols}
                columnLabels={resultLabels}
                onRowClick={makeTableHandler('shoes', 'women', 'eu')}
                activeRow={activeRow}
              />
            </section>

            <section className="mb-10" id="mens-chart">
              <h2 className="text-2xl font-bold text-white mb-3">{t.sections.mens}</h2>
              <p className="text-slate-400 leading-relaxed mb-4">{t.sections.mensText}</p>
              <SizeTable
                title={t.sections.mens}
                data={menClothingSizes}
                columns={['international', 'eu', 'uk', 'us', 'chestCm', 'waistCm']}
                columnLabels={resultLabels}
                onRowClick={makeTableHandler('mens-clothing', 'men', 'international')}
                activeRow={activeRow}
                clickHint={t.table.clickHint}
              />
            </section>

            <section className="mb-10" id="womens-chart">
              <h2 className="text-2xl font-bold text-white mb-3">{t.sections.womens}</h2>
              <p className="text-slate-400 leading-relaxed mb-4">{t.sections.womensText}</p>
              <SizeTable
                title={t.sections.womens}
                data={womenClothingSizes}
                columns={['international', 'eu', 'uk', 'us', 'chestCm', 'waistCm', 'hipsCm']}
                columnLabels={resultLabels}
                onRowClick={makeTableHandler('womens-clothing', 'women', 'international')}
                activeRow={activeRow}
              />
            </section>

            <section className="mb-10" id="jeans-chart">
              <h2 className="text-2xl font-bold text-white mb-3">
                {t.sections.jeans || 'Jeans & pants size chart'}
              </h2>
              <p className="text-slate-400 leading-relaxed mb-4">{t.sections.jeansText}</p>
              <h3 className="text-lg font-semibold text-slate-200 mb-2">Men</h3>
              <SizeTable
                title="Jeans men"
                data={tables.jeans.men.data}
                columns={tables.jeans.men.cols}
                columnLabels={resultLabels}
                onRowClick={makeTableHandler('jeans', 'men', 'waistIn')}
                activeRow={activeRow}
                clickHint={t.table.clickHint}
              />
              <h3 className="text-lg font-semibold text-slate-200 mt-6 mb-2">Women</h3>
              <SizeTable
                title="Jeans women"
                data={tables.jeans.women.data}
                columns={tables.jeans.women.cols}
                columnLabels={resultLabels}
                onRowClick={makeTableHandler('jeans', 'women', 'waistIn')}
                activeRow={activeRow}
              />
            </section>

            <section className="mb-10" id="kids-chart">
              <h2 className="text-2xl font-bold text-white mb-3">{t.sections.kids}</h2>
              <p className="text-slate-400 leading-relaxed mb-4">{t.sections.kidsText}</p>
              <SizeTable
                title={t.sections.kids}
                data={kidsShoeSizes}
                columns={['eu', 'uk', 'us', 'cm', 'age']}
                columnLabels={resultLabels}
                onRowClick={makeTableHandler('kids-shoes', 'kids', 'eu')}
                activeRow={activeRow}
                clickHint={t.table.clickHint}
              />
            </section>

            <section className="mb-10" id="measure-guide">
              <h2 className="text-2xl font-bold text-white mb-3">{t.sections.measure}</h2>
              <ul className="space-y-4">
                {t.sections.measureItems.map((item) => (
                  <li
                    key={item.title}
                    className="rounded-lg border border-slate-700 bg-slate-800/30 p-4"
                  >
                    <strong className="text-emerald-400">{item.title}</strong>
                    <p className="text-slate-400 mt-1 text-sm">{item.text}</p>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-white mb-3">{t.sections.seo}</h2>
              <p className="text-slate-400 leading-relaxed">{t.sections.seoText}</p>
            </section>
          </article>

          <div id="faq">
            <FaqSection title={t.sections.faq} items={t.faq} />
          </div>
        </main>

        <footer className="border-t border-slate-800 py-8 text-center text-slate-500 text-sm space-y-2">
          <p>Approximate size converter guide</p>
          <p>
            <a
              href="https://pixelutility.com"
              rel="noopener noreferrer"
              className="text-emerald-500/80 hover:text-emerald-400 transition-colors"
            >
              Pixel Utility
            </a>
            <span className="text-slate-600 mx-1">·</span>
            <span className="text-slate-600">clothing-shoe-size-converter.pixelutility.com</span>
          </p>
        </footer>
      </div>
    </>
  );
}
